// Pure list-shaping for the facility editor's Elation tags (migration 078).
// CommonJS so Jest can require it directly; esbuild bundles it for the admin
// page via `import { … }`.
//
// The server is authoritative: PATCH /data/facilities/:id stores the list in
// the normalized comparison form (server/services/elationTags.js
// normalizeTagList — trimmed, lower-cased, de-duplicated). This mirrors that
// form so what the editor shows before the save is what the save returns; a
// drift test (tests/unit/facilityElationTags.test.js) holds the two together.

function normalizeTag(raw) {
  return String(raw == null ? '' : raw).trim().toLowerCase();
}

// current list + one typed/picked tag → the next list (unchanged when the tag is
// blank or already present, in any casing).
function addTag(current, raw) {
  const list = Array.isArray(current) ? current.map(normalizeTag).filter(Boolean) : [];
  const tag = normalizeTag(raw);
  if (!tag || list.includes(tag)) return list;
  return [...list, tag];
}

function removeTag(current, raw) {
  const tag = normalizeTag(raw);
  return (Array.isArray(current) ? current : []).map(normalizeTag).filter((t) => t && t !== tag);
}

// A directory tag entry is `{ tag, charts }` (GET /data/facilities/directory-tags);
// a bare string is accepted too, with an unknown count.
function tagEntry(raw) {
  if (raw && typeof raw === 'object') {
    const n = Number(raw.charts);
    return { tag: normalizeTag(raw.tag), charts: Number.isFinite(n) ? n : null };
  }
  return { tag: normalizeTag(raw), charts: null };
}

// "12 charts" / "1 chart" — how many directory charts carry a tag, or a
// facility's tags put in scope.
function chartCount(n) {
  const v = Number(n) || 0;
  return `${v} chart${v === 1 ? '' : 's'}`;
}

// The autocomplete option's label: the tag and its chart count, so a broad tag
// (a program on a whole panel) is visible before it is picked.
function tagOptionLabel(entry) {
  const e = tagEntry(entry);
  return e.charts == null ? e.tag : `${e.tag} (${chartCount(e.charts)})`;
}

// tag → its chart count, from the directory list (for the facility's pills).
function chartsByTag(directoryTags) {
  const out = new Map();
  for (const raw of Array.isArray(directoryTags) ? directoryTags : []) {
    const e = tagEntry(raw);
    if (e.tag && e.charts != null && !out.has(e.tag)) out.set(e.tag, e.charts);
  }
  return out;
}

// Autocomplete: directory tags not already on the facility, filtered by the
// typed text (substring, case-insensitive), capped for the dropdown. Returns
// `{ tag, charts }` entries.
function suggestTags(directoryTags, current, query, limit = 20) {
  const have = new Set((Array.isArray(current) ? current : []).map(normalizeTag));
  const q = normalizeTag(query);
  const out = [];
  const seen = new Set();
  for (const raw of Array.isArray(directoryTags) ? directoryTags : []) {
    const entry = tagEntry(raw);
    const { tag } = entry;
    if (!tag || have.has(tag) || seen.has(tag)) continue;
    if (q && !tag.includes(q)) continue;
    seen.add(tag);
    out.push(entry);
    if (out.length >= limit) break;
  }
  return out;
}

module.exports = { normalizeTag, addTag, removeTag, suggestTags, tagOptionLabel, chartCount, chartsByTag };
